import { suppressKeyboardEventForInput } from 'components/AgGrid/utils';
import type { ContractStatusEnum, WorkOrderActKc2Data, WorkOrderStatusEnum } from 'gql/graphql';
import { ActKc2StatusEnum } from 'gql/graphql';
import { formatActTitle } from 'pages/Project/Common/ActKc2Title/ActKc2Title';
import { getActCellClass, useScrolledToActId } from 'pages/Project/Common/hooks/useScrolledToActId';
import { getNumberFormatter } from 'pages/Project/Common/utils';
import { DeviatedColumnTitle } from 'utils/tableCells';

import { useMemo } from 'react';

import { TitleActKC2Columns } from '../../WorkOrderPrice/TitleActKC2Columns';
import { WorkOrderOtherActEditableCell } from '../WorkOrderOtherEditableCells';

export const useOthersActColumns = (
    acts: WorkOrderActKc2Data[],
    reloadTreeHandler: () => void,
    reloadDataHandler: () => void,
    contractStatus?: ContractStatusEnum,
    workOrderStatus?: WorkOrderStatusEnum,
    isDisabledByProjectStatus?: boolean,
    costRounding?: number,
) => {
    const { scrolledToActId, withHighLightManager } = useScrolledToActId();

    return useMemo(() => {
        let isOddSeason = true;
        const currentSeasonId = acts?.[0]?.seasonId;

        return acts.map(
            ({
                id,
                number,
                date,
                planCostDeviation,
                acceptedCostDeviation,
                seasonId,
                status,
                fullNumber,
                isCorrection,
                isInactive,
                canCreateCorrection,
                correctionDate,
            }) => {
                const isActStatusAccepted = status === ActKc2StatusEnum.Accepted;

                if (seasonId !== currentSeasonId) {
                    isOddSeason = !isOddSeason;
                }

                const cellClass = getActCellClass(id, isOddSeason, scrolledToActId);

                return {
                    id,
                    seasonId,

                    headerGroupComponent: () => (
                        <TitleActKC2Columns
                            id={id}
                            number={number}
                            status={status}
                            date={date}
                            contractStatus={contractStatus}
                            onRefresh={reloadTreeHandler}
                            isDisabledByProjectStatus={isDisabledByProjectStatus}
                            workOrderStatus={workOrderStatus}
                            fullNumber={fullNumber}
                            isCorrection={isCorrection}
                            isInactive={isInactive}
                            canCreateCorrection={canCreateCorrection}
                            correctionDate={correctionDate}
                            withHighLightManager={withHighLightManager}
                        />
                    ),
                    headerName: formatActTitle(
                        isCorrection ? fullNumber : number,
                        isCorrection ? correctionDate : date,
                    ),
                    marryChildren: true,
                    headerClass: `${cellClass}`,
                    cellClass: `ag-right-aligned-cell ${cellClass}`,
                    children: [
                        ...(!isActStatusAccepted
                            ? [
                                  {
                                      headerName: 'Объем (план)',
                                      field: `actKC2CostVolumePlan_${id}`,
                                      headerClass: cellClass,
                                      cellClass: `ag-right-aligned-cell ${cellClass} ${
                                          isInactive ? 'inactive-act' : undefined
                                      }`,
                                      width: 112,
                                  },
                                  {
                                      headerComponent: () => (
                                          <DeviatedColumnTitle
                                              title="Стоимость (план)"
                                              deviation={planCostDeviation}
                                              tooltipText="Превышает остаток по сезону"
                                          />
                                      ),
                                      field: `actKC2PlanCost_${id}`,
                                      headerClass: `ag-right-aligned-header ${cellClass}`,
                                      cellClass: `ag-right-aligned-cell ${cellClass} ${
                                          isInactive ? 'inactive-act' : undefined
                                      }`,
                                      valueGetter: ({ data }: { data: any }) => ({
                                          value: (data as any)[`actKC2PlanCost_${id}`],
                                      }),
                                      valueFormatter: getNumberFormatter(costRounding || 0),
                                      cellRenderer: WorkOrderOtherActEditableCell,
                                      cellRendererParams: {
                                          currentColId: `actKC2PlanCost_${id}`,
                                          deviationKey: `actKC2PlanCostDeviation_${id}`,
                                          isEditable: status === ActKc2StatusEnum.InProgress,
                                          actKCId: id,
                                          isDisabled: isDisabledByProjectStatus,
                                          valueType: 'plan',
                                          decimal: costRounding || 0,
                                          reloadDataHandler,
                                      },
                                      width: 150,
                                      minWidth: 150,
                                      suppressKeyboardEvent: suppressKeyboardEventForInput,
                                  },
                              ]
                            : []),

                        {
                            headerName: 'Объем (согласовано)',
                            headerClass: cellClass,
                            cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                            field: `actKC2CostVolumeAccepted_${id}`,
                            width: 112,
                            minWidth: 140,
                        },
                        {
                            headerComponent: () => (
                                <DeviatedColumnTitle
                                    title="Стоимость (согласовано)"
                                    deviation={acceptedCostDeviation}
                                    tooltipText="Превышает остаток по сезону"
                                />
                            ),
                            field: `actKC2AcceptedCost_${id}`,
                            headerClass: `ag-right-aligned-header ${cellClass}`,
                            cellClass: `ag-right-aligned-cell ${cellClass} ${isInactive ? 'inactive-act' : undefined}`,
                            valueGetter: ({ data }: { data: any }) => ({
                                value: (data as any)[`actKC2AcceptedCost_${id}`],
                            }),
                            valueFormatter: getNumberFormatter(costRounding || 0),
                            cellRenderer: WorkOrderOtherActEditableCell,
                            cellRendererParams: {
                                deviationKey: `actKC2AcceptedCostDeviation_${id}`,
                                isEditable: status === ActKc2StatusEnum.OnCheck,
                                actKCId: id,
                                isDisabled: isDisabledByProjectStatus,
                                valueType: 'accepted',
                                currentColId: `actKC2AcceptedCost_${id}`,
                                decimal: costRounding || 0,
                                reloadDataHandler,
                            },
                            width: 150,
                            minWidth: 150,
                            suppressKeyboardEvent: suppressKeyboardEventForInput,
                        },
                    ],
                };
            },
        );
    }, [
        acts,
        scrolledToActId,
        costRounding,
        isDisabledByProjectStatus,
        reloadDataHandler,
        contractStatus,
        reloadTreeHandler,
        workOrderStatus,
        withHighLightManager,
    ]);
};
